import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/layout/DashboardLayout';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { studentAPI } from '../../services/api';
import { toast } from 'sonner';
import { MultiSelect } from '../../components/shared/MultiSelect';
import { ImageUpload } from '../../components/shared/ImageUpload';
import { DocumentUpload } from '../../components/shared/DocumentUpload';
import {
  User,
  GraduationCap,
  FileText,
  Target,
  Save,
  Loader2,
  ArrowLeft,
} from 'lucide-react';

// ─── Constants ───────────────────────────────────────────────────────────────

const COUNTRY_OPTIONS = [
  { value: 'USA', label: 'United States' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Australia', label: 'Australia' },
  { value: 'Germany', label: 'Germany' },
  { value: 'France', label: 'France' },
  { value: 'Netherlands', label: 'Netherlands' },
  { value: 'Sweden', label: 'Sweden' },
  { value: 'Ireland', label: 'Ireland' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'Japan', label: 'Japan' },
  { value: 'South Korea', label: 'South Korea' },
  { value: 'New Zealand', label: 'New Zealand' },
  { value: 'Switzerland', label: 'Switzerland' },
  { value: 'Italy', label: 'Italy' },
  { value: 'Spain', label: 'Spain' },
];

const DOCUMENT_TYPES = [
  { type: 'transcripts', label: 'Transcripts' },
  { type: 'testScores', label: 'Test Scores' },
  { type: 'sop', label: 'Statement of Purpose (SOP)' },
  { type: 'recommendation', label: 'Recommendation Letters' },
  { type: 'resume', label: 'Resume/CV' },
  { type: 'passport', label: 'Passport' },
];

// ─── Animation variants ───────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function MyProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // State
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState({});
  const [errors, setErrors] = useState({});
  const [pendingImage, setPendingImage] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    age: '',
    currentEducationLevel: '',
    fieldOfStudy: '',
    gpa: '',
    graduationYear: '',
    institution: '',
    ielts: '',
    toefl: '',
    gre: '',
    preferredCountries: [],
    preferredDegreeLevel: '',
    budget: '',
    careerGoals: '',
    industry: '',
    workLocation: '',
  });

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await studentAPI.getMyProfile();
      const profileData = response.student;
      setProfile(profileData);
      setFormData({
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
        phone: profileData.phone || '',
        country: profileData.country || '',
        age: profileData.age || '',
        currentEducationLevel: profileData.currentEducationLevel || '',
        fieldOfStudy: profileData.fieldOfStudy || '',
        gpa: profileData.gpa || '',
        graduationYear: profileData.graduationYear || '',
        institution: profileData.institution || '',
        ielts: profileData.ielts || '',
        toefl: profileData.toefl || '',
        gre: profileData.gre || '',
        preferredCountries: profileData.preferredCountries || [],
        preferredDegreeLevel: profileData.preferredDegreeLevel || '',
        budget: profileData.budget || '',
        careerGoals: profileData.careerGoals || '',
        industry: profileData.industry || '',
        workLocation: profileData.workLocation || '',
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phone: profile.phone || '',
        country: profile.country || '',
        age: profile.age || '',
        currentEducationLevel: profile.currentEducationLevel || '',
        fieldOfStudy: profile.fieldOfStudy || '',
        gpa: profile.gpa || '',
        graduationYear: profile.graduationYear || '',
        institution: profile.institution || '',
        ielts: profile.ielts || '',
        toefl: profile.toefl || '',
        gre: profile.gre || '',
        preferredCountries: profile.preferredCountries || [],
        preferredDegreeLevel: profile.preferredDegreeLevel || '',
        budget: profile.budget || '',
        careerGoals: profile.careerGoals || '',
        industry: profile.industry || '',
        workLocation: profile.workLocation || '',
      });
    }
    setErrors({});
    setPendingImage(null);
    navigate('/dashboard');
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleMultiSelectChange = value => {
    setFormData(prev => ({ ...prev, preferredCountries: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (formData.age && (isNaN(formData.age) || formData.age < 0 || formData.age > 120)) {
      newErrors.age = 'Please enter a valid age';
    }
    if (formData.graduationYear) {
      const year = parseInt(formData.graduationYear);
      if (isNaN(year) || year < 1900 || year > new Date().getFullYear() + 10) {
        newErrors.graduationYear = 'Please enter a valid graduation year';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    setSaving(true);
    try {
      if (pendingImage) {
        try {
          await studentAPI.uploadProfileImage(pendingImage);
          setPendingImage(null);
        } catch (imgError) {
          console.error('Image upload failed:', imgError);
          toast.error('Failed to upload profile image');
        }
      }
      const response = await studentAPI.updateMyProfile(formData);
      setProfile(response.student);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = file => setPendingImage(file);

  const handleDocumentUpload = async (file, documentType) => {
    setUploadingDoc(prev => ({ ...prev, [documentType]: true }));
    try {
      const response = await studentAPI.uploadDocument(file, documentType);
      setProfile(prev => ({
        ...prev,
        documents: { ...prev.documents, [documentType]: response.file.url },
      }));
      toast.success(response.message || 'Document uploaded successfully');
    } catch (error) {
      console.error('Document upload failed:', error);
      toast.error(error.response?.data?.error || 'Failed to upload document');
      throw error;
    } finally {
      setUploadingDoc(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const handleDocumentDelete = async documentType => {
    try {
      await studentAPI.deleteDocument(documentType);
      setProfile(prev => ({
        ...prev,
        documents: { ...prev.documents, [documentType]: null },
      }));
      toast.success('Document deleted successfully');
    } catch (error) {
      console.error('Document delete failed:', error);
      toast.error(error.response?.data?.error || 'Failed to delete document');
      throw error;
    }
  };

  // ─── Loading ────────────────────────────────────────────────────────────────

  if (loading && !profile) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="min-h-full bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 -m-4 lg:-m-8 p-8">
        <div className="mx-auto max-w-5xl">

          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
            <p className="text-gray-600 mt-2">
              Manage your personal information and academic profile
            </p>
          </motion.div>

          <form onSubmit={handleSubmit}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-6"
            >

              {/* Profile Picture */}
              <motion.div variants={itemVariants}>
                <Card className="backdrop-blur-sm bg-white/80 border-white/20 shadow-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      Profile Picture
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ImageUpload
                      currentImage={profile?.avatar}
                      onImageSelect={handleImageSelect}
                      disabled={saving}
                    />
                    {pendingImage && (
                      <p className="text-sm text-amber-600 font-medium text-center mt-3 bg-amber-50 py-2 px-4 rounded-lg border border-amber-100">
                        New image selected — click "Save Changes" to upload.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Personal Information */}
              <motion.div variants={itemVariants}>
                <Card className="backdrop-blur-sm bg-white/80 border-white/20 shadow-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      Personal Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="Enter first name"
                          disabled={saving}
                          className={errors.firstName ? 'border-red-500' : ''}
                        />
                        {errors.firstName && (
                          <p className="text-xs text-red-500">{errors.firstName}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Enter last name"
                          disabled={saving}
                          className={errors.lastName ? 'border-red-500' : ''}
                        />
                        {errors.lastName && (
                          <p className="text-xs text-red-500">{errors.lastName}</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profile?.email || user?.email || ''}
                        disabled
                        className="bg-slate-50/80"
                      />
                      <p className="text-xs text-slate-400">Email cannot be changed here</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+1 234 567 8900"
                          disabled={saving}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country">Country</Label>
                        <Input
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          placeholder="Your home country"
                          disabled={saving}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Education Information */}
              <motion.div variants={itemVariants}>
                <Card className="backdrop-blur-sm bg-white/80 border-white/20 shadow-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-blue-600" />
                      Education Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="age">Age</Label>
                        <Input
                          id="age"
                          name="age"
                          type="number"
                          value={formData.age}
                          onChange={handleInputChange}
                          placeholder="Your age"
                          min="0"
                          max="120"
                          disabled={saving}
                          className={errors.age ? 'border-red-500' : ''}
                        />
                        {errors.age && (
                          <p className="text-xs text-red-500">{errors.age}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Current Education Level</Label>
                        <Select
                          value={formData.currentEducationLevel}
                          onValueChange={v => handleSelectChange('currentEducationLevel', v)}
                          disabled={saving}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="diploma">Diploma</SelectItem>
                            <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                            <SelectItem value="master">Master's Degree</SelectItem>
                            <SelectItem value="phd">PhD</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="fieldOfStudy">Field of Study</Label>
                        <Input
                          id="fieldOfStudy"
                          name="fieldOfStudy"
                          value={formData.fieldOfStudy}
                          onChange={handleInputChange}
                          placeholder="e.g., Computer Science"
                          disabled={saving}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="institution">Institution</Label>
                        <Input
                          id="institution"
                          name="institution"
                          value={formData.institution}
                          onChange={handleInputChange}
                          placeholder="Your university/college"
                          disabled={saving}
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="gpa">GPA</Label>
                        <Input
                          id="gpa"
                          name="gpa"
                          value={formData.gpa}
                          onChange={handleInputChange}
                          placeholder="e.g., 3.8/4.0"
                          disabled={saving}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="graduationYear">Graduation Year</Label>
                        <Input
                          id="graduationYear"
                          name="graduationYear"
                          type="number"
                          value={formData.graduationYear}
                          onChange={handleInputChange}
                          placeholder="2024"
                          min="1900"
                          max={new Date().getFullYear() + 10}
                          disabled={saving}
                          className={errors.graduationYear ? 'border-red-500' : ''}
                        />
                        {errors.graduationYear && (
                          <p className="text-xs text-red-500">{errors.graduationYear}</p>
                        )}
                      </div>
                    </div>

                    <Separator className="my-2" />

                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-700">Test Scores</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="ielts">IELTS Score</Label>
                          <Input
                            id="ielts"
                            name="ielts"
                            value={formData.ielts}
                            onChange={handleInputChange}
                            placeholder="e.g., 7.5"
                            disabled={saving}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="toefl">TOEFL Score</Label>
                          <Input
                            id="toefl"
                            name="toefl"
                            value={formData.toefl}
                            onChange={handleInputChange}
                            placeholder="e.g., 100"
                            disabled={saving}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="gre">GRE Score</Label>
                          <Input
                            id="gre"
                            name="gre"
                            value={formData.gre}
                            onChange={handleInputChange}
                            placeholder="e.g., 320"
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Study Preferences & Career Goals */}
              <motion.div variants={itemVariants}>
                <Card className="backdrop-blur-sm bg-white/80 border-white/20 shadow-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-600" />
                      Study Preferences & Career Goals
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <MultiSelect
                        label="Preferred Countries"
                        options={COUNTRY_OPTIONS}
                        value={formData.preferredCountries}
                        onChange={handleMultiSelectChange}
                        placeholder="Select countries you're interested in"
                        disabled={saving}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Preferred Degree Level</Label>
                        <Select
                          value={formData.preferredDegreeLevel}
                          onValueChange={v => handleSelectChange('preferredDegreeLevel', v)}
                          disabled={saving}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select degree level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                            <SelectItem value="master">Master's Degree</SelectItem>
                            <SelectItem value="phd">PhD</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="budget">Budget (Annual)</Label>
                        <Input
                          id="budget"
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          placeholder="e.g., $30,000 - $50,000"
                          disabled={saving}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="careerGoals">Career Goals</Label>
                      <Textarea
                        id="careerGoals"
                        name="careerGoals"
                        value={formData.careerGoals}
                        onChange={handleInputChange}
                        placeholder="Describe your career aspirations and goals..."
                        rows={4}
                        disabled={saving}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Target Industry</Label>
                        <Select
                          value={formData.industry}
                          onValueChange={v => handleSelectChange('industry', v)}
                          disabled={saving}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tech">Technology</SelectItem>
                            <SelectItem value="finance">Finance</SelectItem>
                            <SelectItem value="healthcare">Healthcare</SelectItem>
                            <SelectItem value="education">Education</SelectItem>
                            <SelectItem value="consulting">Consulting</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Preferred Work Location</Label>
                        <Select
                          value={formData.workLocation}
                          onValueChange={v => handleSelectChange('workLocation', v)}
                          disabled={saving}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="home-country">Home Country</SelectItem>
                            <SelectItem value="study-country">Study Country</SelectItem>
                            <SelectItem value="global">Global/Flexible</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Documents */}
              <motion.div variants={itemVariants}>
                <Card className="backdrop-blur-sm bg-white/80 border-white/20 shadow-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {DOCUMENT_TYPES.map(({ type, label }) => (
                        <DocumentUpload
                          key={type}
                          label={label}
                          documentType={type}
                          currentFile={profile?.documents?.[type]}
                          onUpload={handleDocumentUpload}
                          onDelete={handleDocumentDelete}
                          disabled={saving || uploadingDoc[type]}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Action Buttons */}
              <motion.div variants={itemVariants} className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving Changes...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </motion.div>

            </motion.div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
